/**
 * Main interface for the 'Suntap' garden submission workflow.
 * Allows users to search for pubs, load 3D mesh data for the selected
 * location, and initiate the garden drawing process. Handles the submission
 * of garden polygon data and coordinates the overall user experience from
 * pub selection to garden definition. Integrates with the 3D drawing tools
 * to enable pub garden area mapping.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../config';
import TopNav from './TopNav';
import Fonts from './Fonts';
import { Footer } from './Footer';
import { PubObject } from "./FindPubs";
import { 
  AppAuth, 
  fetchMinimalMeshes, 
  deleteGarden, 
  updateGardenUncertainty, 
  markPubHasNoGarden, 
  submitGardenToDatabase,
  isElevationChangeOperation,
  latLonElevToECEF,
  gardenCallbackECEFToLatLonElev,
  GardenSubArea,
  getNearbyPubs,
  getPubByGooglePlaceId,
  getSubmitterName,
  getStoredTimezone
} from '../API';
import { Garden } from '../types/gardenTypes'; // Import Garden type from local types
import { gardenStyles } from '../styles/GardenSubmitStyles';
import { themeColors } from '../styles/theme';
import GardenDraw3DController from './garden_draw/GardenDrawController';
import { trackGardenSubmissionType, trackViewResultsAfterSubmission, trackGardenSubmissionConversion, trackNoGardenMarked } from '../utils/analytics';
import Loader from './Loader'; // Import Loader component
import PubSearchGoogle, { GooglePubResult } from './PubSearchGoogle'; // Import the Google search component

const SubmitGarden3D: React.FC = () => {
  const theme = useTheme();
  const styles = gardenStyles(theme);
  const location = useLocation();
  const navigate = useNavigate();

  // Ref to track if the initial pub selection has been processed
  const initialPubProcessedRef = useRef(false);

  // Helper function to clear the raycasting cache for a specific pub
  const clearPubCache = useCallback((google_place_id: string) => {
      if (!google_place_id) return;
      
      // Get the user's timezone
      const timezone = getStoredTimezone();
      
      // Clear both detail and simple cache keys with timezone
      const detailCacheKey = `pub_raycasting_${google_place_id}_detail_${timezone}`;
      const simpleCacheKey = `pub_raycasting_${google_place_id}_simple_${timezone}`;
      
      try {
        localStorage.removeItem(`cache_${detailCacheKey}`);
        localStorage.removeItem(`cache_${simpleCacheKey}`);
        console.log(`Cache cleared for pub ${google_place_id} in timezone ${timezone}`);
      } catch (err) {
        console.error('Error clearing cache:', err);
      }
  }, []);

  useEffect(() => {
    // Ensure the page is scrolled to the top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // State
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedPub, setSelectedPub] = useState<PubObject | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [meshUris, setMeshUris] = useState<string[]>([]);
  const [isPubPreselected, setIsPubPreselected] = useState<boolean>(false);
  const [fetchingMeshes, setFetchingMeshes] = useState<boolean>(false);
  const [showViewer, setShowViewer] = useState<boolean>(false);
  const [drawnPolygonCoordinates, setDrawnPolygonCoordinates] = useState<[number, number, number][]>([]);
  const [latLonCoordinates, setLatLonCoordinates] = useState<[number, number, number][]>([]);
  const [gardenElevation, setGardenElevation] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [gardenName, setGardenName] = useState<string>('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [noGardenConfirmDialog, setNoGardenConfirmDialog] = useState<boolean>(false);
  const [existingGarden, setExistingGarden] = useState<GardenSubArea | null>(null);
  const [pendingGardenSubmission, setPendingGardenSubmission] = useState<{
    convertedCoordinates: [number, number, number][];
    gardenNameValue: string;
    avgElevation: number;
    isUncertain: boolean;
  } | null>(null);
  const [lastDrawnGardenName, setLastDrawnGardenName] = useState<string>('');
  const [reloadGardens, setReloadGardens] = useState<number>(0); // Helper to track garden reloads
  const [attributionOpen, setAttributionOpen] = useState<boolean>(false);
  const [copyrights, setCopyrights] = useState<string[]>([]);
  
  // New state for the "View Results" modal
  const [showViewResultsModal, setShowViewResultsModal] = useState<boolean>(false);
  
  // State to track the number of gardens loaded (for showing/hiding the "View pub results" button)
  const [gardenCount, setGardenCount] = useState<number>(0);

  // New state for the submitter name modal and submitter name
  const [submitterNameModal, setSubmitterNameModal] = useState<boolean>(false);
  const [submitterName, setSubmitterName] = useState<string>(getSubmitterName());
  const [pendingSubmission, setPendingSubmission] = useState<any>(null);

  // New state to hold the focal point after it's been adjusted to the mesh surface
  const [adjustedFocalPoint, setAdjustedFocalPoint] = useState<[number, number, number] | null>(null);

  // Memoized focal point. Prefers the adjusted point if available.
  const memoizedFocalPoint = useMemo<[number, number, number] | undefined>(() => {
    if (adjustedFocalPoint) {
      return adjustedFocalPoint;
    }
    if (!selectedPub) return undefined;
    return latLonElevToECEF(
      selectedPub.latitude || 0,
      selectedPub.longitude || 0,
    );
  }, [selectedPub?.latitude, selectedPub?.longitude, adjustedFocalPoint]);

  // Callback for when the viewer adjusts the focal point to the mesh
  const handleFocalPointAdjusted = useCallback((newFocalPointEcef: [number, number, number]) => {
      // The viewer has found the ground. We now have the correct ECEF coordinates.
      // We set this in state, which will be fed back into the viewer as the true focal point.
      setAdjustedFocalPoint(newFocalPointEcef);

      // We also update the pub's lat/lon for data consistency, without triggering a re-adjustment cycle.
      // gardenCallbackECEFToLatLonElev returns [lat, lon, elev].
      const [lat, lon] = gardenCallbackECEFToLatLonElev(
          newFocalPointEcef[0],
          newFocalPointEcef[1],
          newFocalPointEcef[2]
      );

      setSelectedPub(currentPub => {
          // Ensure we assign lat to latitude and lon to longitude.
          if (currentPub && (currentPub.latitude !== lat || currentPub.longitude !== lon)) {
              return { ...currentPub, latitude: lat, longitude: lon };
          }
          return currentPub;
      });
  }, []);

  // Fetch Mesh Data
  const fetchMeshData = useCallback(async (pubToLoad?: PubObject) => {
    const pub = pubToLoad || selectedPub;
    if (!pub) {
      setErrorMessage('No pub selected.');
      setLoading(false); // Ensure loading is turned off if there's an error
      return;
    }
    
    // Check if we have valid latitude and longitude
    if (!pub.latitude || !pub.longitude || pub.latitude === 0 || pub.longitude === 0) {
      setErrorMessage('Invalid pub coordinates. Please select a pub with valid location data.');
      setLoading(false); // Ensure loading is turned off if there's an error
      return;
    }
    
    setFetchingMeshes(true);
    setErrorMessage('');
    setSuccessMessage('');
    setMeshUris([]);
    setShowViewer(false); // Hide viewer initially while fetching
    try {
      const result = await fetchMinimalMeshes(pub.latitude, pub.longitude);
      const meshUriPaths = result?.mesh_uris;
      const sessionId = result?.session_id;
      const copyrights = result?.copyrights || [];  // Attribution data
      setCopyrights(copyrights); // Store copyrights for attribution dialog
      const googleTilesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  
      if (meshUriPaths && meshUriPaths.length > 0 && sessionId && googleTilesApiKey) {
        const baseUrl = 'https://tile.googleapis.com';
        const completeUrls = meshUriPaths.map((uri: string) => {
          const path = uri.startsWith('/') ? uri : `/${uri}`;
          const sessionQuery = sessionId.includes('session=') ? sessionId : `session=${sessionId}`;
          return `${baseUrl}${path}?${sessionQuery}&key=${googleTilesApiKey}`;
        });
        setMeshUris(completeUrls);
        setShowViewer(true); // Show viewer only after successful fetch and URL creation
        setSuccessMessage(`Loaded 3D map for ${pub.name}. Ready to draw.`);
      } else {
        let errorMsg = 'Could not load 3D map data for this location.';
        if (!googleTilesApiKey) errorMsg += ' (Missing API key)';
        else if (!sessionId) errorMsg += ' (Invalid session)';
        else if (!meshUriPaths || meshUriPaths.length === 0) errorMsg += ' (No meshes found)';
        setErrorMessage(errorMsg);
        setShowViewer(false); // Ensure viewer stays hidden on error
      }
    } catch (error: any) {
      console.error('Error fetching mesh data:', error);
      setErrorMessage(`Failed to fetch mesh data: ${error.message || 'Unknown error'}`);
      setShowViewer(false); // Ensure viewer stays hidden on error
    } finally {
      setFetchingMeshes(false);
      setLoading(false); // Ensure we turn off the main loading indicator when mesh fetching is done
    }
  }, [selectedPub]); // Dependency only on selectedPub

  // Handle Pub Selection
  const handlePubSelect = useCallback(async (pub: PubObject | null) => {
    setAdjustedFocalPoint(null); // Reset the adjusted focal point for the new pub
    setSelectedPub(pub);
    // Reset states related to drawing and meshes
    setMeshUris([]);
    setDrawnPolygonCoordinates([]);
    setLatLonCoordinates([]);
    setGardenElevation(null);
    setGardenName('');
    setLastDrawnGardenName('');
    setSuccessMessage('');
    setErrorMessage('');
    setShowViewer(false); // Hide viewer when pub changes
    setReloadGardens(prev => prev + 1); // Trigger potential viewer key change if it was shown

    if (pub) {
        // Check if we have all the data we need already
        const hasRequiredData = pub.name && 
                               pub.google_place_id && 
                               pub.latitude !== undefined && 
                               pub.longitude !== undefined;
                               
        if (hasRequiredData) {
            // Use existing data without additional API calls
            await fetchMeshData(pub);
        } else {
            // Only fetch additional data if necessary
            try {
                const response = await getPubByGooglePlaceId(pub.google_place_id);
                if (response.data && response.data.pub) {
                    // Merge the new data with existing pub data
                    const completeData = {
                        ...pub,
                        name: pub.name || response.data.pub.name,
                        latitude: pub.latitude || response.data.pub.latitude,
                        longitude: pub.longitude || response.data.pub.longitude,
                        address: pub.address || response.data.pub.address
                    };
                    setSelectedPub(completeData);
                    await fetchMeshData(completeData);
                }
            } catch (error) {
                console.error('Error fetching complete pub data:', error);
                // Still try with the data we have
                await fetchMeshData(pub);
            }
        }
    }
}, [fetchMeshData]);

  // Add the Google Pub selection handler
  const handleGooglePubSelect = useCallback((googlePub: GooglePubResult) => {
    // Transform GooglePubResult to Pub
    const transformedPub: PubObject = {
      name: googlePub.name,
      google_place_id: googlePub.google_place_id,
      address: googlePub.formatted_address || googlePub.address || '',
      latitude: googlePub.latitude,
      longitude: googlePub.longitude,
      distance: 0,
      garden_drawn: false,
      garden_moderated: false,
      has_no_garden: false
    };
    
    // Set the search query to the selected pub name
    setSearchQuery(googlePub.name);
    
    // Call the existing handler
    handlePubSelect(transformedPub);
  }, [handlePubSelect]);

  // Pre-select Pub from Location State (e.g., navigating from map)
  useEffect(() => {
    // Only run once on component mount or when URL changes
    if (!initialPubProcessedRef.current) {
      initialPubProcessedRef.current = true;
      
      // Check query parameters for pub_id
      const queryParams = new URLSearchParams(location.search);
      const googlePlaceId = queryParams.get('pub_id');
      
      if (googlePlaceId) {
        // Check if we have pub data in location.state
        if (location.state && location.state.selectedPub) {
          // Use data from state if available
          const pubFromState = location.state.selectedPub;
          ////console.log("Found pub in state with pub_id in URL:", pubFromState);
          
          // Create a pub object from state data
          const pub: PubObject = {
            name: pubFromState.name,
            google_place_id: pubFromState.google_place_id,
            address: pubFromState.address || '',
            latitude: pubFromState.latitude || pubFromState.lat || 0,
            longitude: pubFromState.longitude || pubFromState.lon || 0,
            distance: pubFromState.distance || 0,
            garden_drawn: pubFromState.garden_drawn || false,
            garden_moderated: pubFromState.garden_moderated || false,
            has_no_garden: pubFromState.has_no_garden || false,
          };
          
          // Validate coordinates
          if (pub.latitude && pub.longitude && pub.latitude !== 0 && pub.longitude !== 0) {
            ////console.log("Using pub data from state:", pub);
            setSelectedPub(pub);
            setIsPubPreselected(true);
            setLoading(true); // Keep loading while fetching meshes
            
            // Fetch meshes for this pub
            setTimeout(() => {
              fetchMeshData(pub).finally(() => {
                setLoading(false);
              });
            }, 100);
            
            // Clear the state after using it
            navigate(location.pathname + location.search, { replace: true, state: {} });
          } else {
            setErrorMessage("Selected pub has invalid coordinates. Please search for it using the search bar.");
            setLoading(false);
          }
        } else {
          // If no valid state data, check if this is a Google Place ID
          setLoading(true);
          
          // Check if it looks like a Google Place ID (usually starts with ChI)
          if (googlePlaceId.startsWith('ChI') || googlePlaceId.includes('-')) {
            // Try to get pub by Google Place ID
            getPubByGooglePlaceId(googlePlaceId)
              .then(response => {
                if (response.data && response.data.exists === false) {
                  // If pub doesn't exist in our database but is a valid Google Place ID
                  // we'll still set it up for searching via Google Places
                  setErrorMessage("This pub isn't in our database yet. Please search for it using the search bar.");
                  setLoading(false);
                  
                  // Pre-populate the search field with the pub ID
                  // which the Google search will use more effectively
                  setSearchQuery(googlePlaceId);
                } else if (response.data && response.data.pub) {
                  // Pub exists in our database
                  const pubData = response.data.pub;
                  
                  // Check if we have valid coordinates
                  if (pubData.latitude && pubData.longitude &&
                      pubData.latitude !== 0 && pubData.longitude !== 0) {
                    
                    // Create a pub object
                    const pub: PubObject = {
                      name: pubData.name,
                      google_place_id: pubData.google_place_id,
                      address: pubData.address || '',
                      latitude: pubData.latitude,
                      distance: pubData.distance || 0,
                      longitude: pubData.longitude,
                      garden_drawn: pubData.garden_drawn || false,
                      garden_moderated: pubData.garden_moderated || false,
                      has_no_garden: pubData.has_no_garden || false,
                    };
                    
                    ////console.log("Using pub data from Google Place ID lookup:", pub);
                    setSelectedPub(pub);
                    setIsPubPreselected(true);
                    
                    // Fetch meshes for this pub
                    setTimeout(() => {
                      fetchMeshData(pub).finally(() => {
                        setLoading(false);
                      });
                    }, 100);
                  } else {
                    // Invalid coordinates
                    setErrorMessage("The pub in our database has invalid coordinates. Please search for it using the search bar.");
                    setLoading(false);
                  }
                } else {
                  // Unexpected response
                  setErrorMessage("Could not find pub information. Please search for it using the search bar.");
                  setLoading(false);
                }
              })
              .catch(error => {
                console.error("Error fetching pub by Google Place ID:", error);
                setErrorMessage("Error finding pub information. Please search for it using the search bar.");
                setLoading(false);
              });
          }
        }
      } else if (location.state && location.state.selectedPub) {
        // Handle case when only state has pub data (no pub_id in URL)
        const pubFromState = location.state.selectedPub;
        //console.log("Received pub from state:", pubFromState);
        
        // Create a pub object from state data
        const pub: PubObject = {
          name: pubFromState.name,
          google_place_id: pubFromState.google_place_id,
          address: pubFromState.address || '',
          distance: pubFromState.distance || 0,
          latitude: pubFromState.latitude || pubFromState.lat || 0,
          longitude: pubFromState.longitude || pubFromState.lon || 0, 
          garden_drawn: pubFromState.garden_drawn || false,
          garden_moderated: pubFromState.garden_moderated || false,
          has_no_garden: pubFromState.has_no_garden || false,
        };
        
        // Validate coordinates
        if (pub.latitude && pub.longitude && pub.latitude !== 0 && pub.longitude !== 0) {
          handlePubSelect(pub);
          setIsPubPreselected(true);
          
          // Clear the state after using it
          navigate(location.pathname, { replace: true, state: {} });
        } else {
          setErrorMessage("Selected pub has invalid coordinates. Please search for it using the search bar.");
          setLoading(false);
        }
      } else {
        // No pub ID or state data - show search interface
        setLoading(false);
      }
    }
  }, [location, navigate, handlePubSelect, fetchMeshData]);

  // Handler for the "View Results" button
  const handleViewResults = useCallback(() => {

    // TODO: track drawingTimeRef
    const timeSpentDrawing = 0;
    trackViewResultsAfterSubmission(selectedPub?.google_place_id || '', timeSpentDrawing);

    // Close the modal if it's open
    setShowViewResultsModal(false);
    
    // Navigate to the ViewPub component for the current pub
    if (selectedPub?.google_place_id) {
      // Navigate to pub view page with the correct path and pub data in state
      navigate(`/pub/${selectedPub.google_place_id}`, { 
        state: { 
          pubToView: selectedPub 
        } 
      });
    }
  }, [navigate, selectedPub]);

  // Handle Garden Completion
  const handleGardenComplete = useCallback(async (coordinates: [number, number, number][], drawnGardenName?: string, isUncertain: boolean = false) => {
    const fromNoGardenView = selectedPub?.has_no_garden || false;
    trackGardenSubmissionConversion(selectedPub?.google_place_id || '', fromNoGardenView);
    
    setDrawnPolygonCoordinates(coordinates); // Store raw ECEF coordinates if needed later
  
    let gardenNameValue = drawnGardenName || 'Garden'; // Default name if not provided
    let existingGardenUid: string | undefined = undefined;
    let isUpdateOperation = false;
  
    // Check for update format "name::uid"
    if (gardenNameValue && gardenNameValue.includes('::')) {
      const parts = gardenNameValue.split('::');
      gardenNameValue = parts[0] || 'Garden'; // Use name part or default
      existingGardenUid = parts[1];
      isUpdateOperation = true;
      //console.log(`Updating existing garden: ${gardenNameValue} (${existingGardenUid})`);
    } else {
      // Track non-default names for new gardens
      if (gardenNameValue && gardenNameValue !== 'Garden') {
        setLastDrawnGardenName(gardenNameValue);
      }
    }
    
    // Add analytics tracking for submission type
    trackGardenSubmissionType(
      selectedPub?.google_place_id || '', 
      isUpdateOperation ? 'update' : 'new', 
      coordinates.length
    );
    
    setGardenName(gardenNameValue); // Store the potentially modified name
    //console.log(`Processing garden: Name="${gardenNameValue}", Uncertain=${isUncertain}, Points=${coordinates.length}`);

    if (coordinates.length < 3) {
        setErrorMessage('Garden boundary must have at least 3 points.');
        setSuccessMessage('');
        return;
    }
     if (!selectedPub) {
        setErrorMessage('No pub selected. Cannot save garden.');
        setSuccessMessage('');
        return;
    }


    try {
        const convertedCoordinates = coordinates.map(coord =>
            gardenCallbackECEFToLatLonElev(coord[0], coord[1], coord[2])
        );

        //console.log(`"${gardenNameValue}" coordinates (Lat/Lon/Elevation):`);
        convertedCoordinates.forEach((coord, index) => {
            //console.log(`  Point ${index + 1}: Lat: ${coord[0].toFixed(6)}, Lon: ${coord[1].toFixed(6)}, Elev: ${coord[2].toFixed(2)}m`);
        });

        setLatLonCoordinates(convertedCoordinates); // Store for potential use/display

        const avgElevation = convertedCoordinates.reduce((sum, coord) => sum + coord[2], 0) / convertedCoordinates.length;
        setGardenElevation(avgElevation); // Store calculated average elevation

        // Prepare for submission
         const submissionData = {
            convertedCoordinates,
            gardenNameValue,
            avgElevation,
            isUncertain,
            existingGardenUid
        };

        // Store the submission data and show the submitter name modal
        setPendingSubmission(submissionData);
        setSubmitterNameModal(true);
        return;

        // The rest of the function is replaced by the processFinalSubmission function
        // that will be called after collecting the submitter's name
    } catch (conversionError) {
        console.error("Error converting ECEF to Lat/Lon/Elev:", conversionError);
        setErrorMessage(`Error processing coordinates: ${conversionError instanceof Error ? conversionError.message : 'Unknown error'}`);
        setSuccessMessage('');
    }
  }, [selectedPub, lastDrawnGardenName]); // Added lastDrawnGardenName dependency

  // Handlers for Confirmation Dialog
  const handleOverwriteGarden = async () => {
    if (pendingGardenSubmission && existingGarden && selectedPub) {
        //console.log(`User chose to overwrite existing garden UID: ${existingGarden.uid}`);
        setPendingSubmission({
          ...pendingGardenSubmission,
          existingGardenUid: existingGarden.uid
        });
        setSubmitterNameModal(true);
        setOpenConfirmDialog(false);
    } else {
        setOpenConfirmDialog(false);
        setPendingGardenSubmission(null);
        setExistingGarden(null);
    }
  };

  const handleCreateNewGarden = async () => {
    if (pendingGardenSubmission && selectedPub) {
        //console.log(`User chose to create a new garden with the same name: "${pendingGardenSubmission.gardenNameValue}"`);
        setPendingSubmission({
          ...pendingGardenSubmission,
          existingGardenUid: undefined
        });
        setSubmitterNameModal(true);
        setOpenConfirmDialog(false);
    } else {
        setOpenConfirmDialog(false);
        setPendingGardenSubmission(null);
        setExistingGarden(null);
    }
  };

  const handleCloseConfirmDialog = () => {
      setOpenConfirmDialog(false);
      setPendingGardenSubmission(null);
      setExistingGarden(null);
       //console.log("Overwrite/Create New dialog cancelled by user.");
      // Potentially clear the drawn polygon in the viewer? Or let user decide?
      // For now, just close dialog and clear pending state.
  }

  // Handle Garden Deletion (from GardenDraw3D)
  const handleGardenDelete = useCallback(async (gardenUid: string) => {
    if (!selectedPub || !gardenUid) {
      setErrorMessage('Cannot delete garden: Missing pub or garden ID.');
      return;
    }

    setIsSubmitting(true); // Use submitting state for visual feedback
    setErrorMessage('');
    setSuccessMessage(`Deleting garden...`); // Indicate action

    try {
        const response = await deleteGarden(gardenUid);

        if (response.status === 200 || response.status === 204) { // 204 No Content is also common for DELETE
            setSuccessMessage('Garden successfully deleted!');
            clearPubCache(selectedPub.google_place_id); // Clear cache

            // Trigger reload in GardenDraw3D to update list
            setTimeout(() => {
                setReloadGardens(prev => prev + 1);
                // Clear success message after reload starts
                setSuccessMessage('');
            }, 800);
        } else {
            throw new Error(`Unexpected response status: ${response.status}`);
        }
    } catch (error) {
        console.error('Error deleting garden:', error);
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        const detailedError = (axios.isAxiosError(error) && error.response?.data?.detail)
            ? error.response.data.detail
            : errorMsg;
        setErrorMessage(`Failed to delete garden: ${detailedError}`);
        setSuccessMessage(''); // Clear optimistic message
    } finally {
        setIsSubmitting(false);
    }
  }, [selectedPub]);

   // Handle Toggling Uncertainty (from GardenDraw3D)
   const handleToggleUncertainty = useCallback((garden: Garden, isUncertain: boolean) => {
    if (!selectedPub || !garden.uid) {
        setErrorMessage('Cannot update garden uncertainty: Missing pub or garden ID.');
        return;
    }
    if (!garden.garden_latlon || garden.garden_latlon.length < 3) {
        setErrorMessage('Cannot update garden uncertainty: Existing garden data is incomplete.');
        return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage(`Updating uncertainty for "${garden.name || 'Garden'}"...`);
    
    // Convert the Garden type from local types to the API expected format
    const apiGarden = {
        uid: garden.uid,
        name: garden.name,
        garden_latlon: garden.garden_latlon as [number, number][] | [number, number, number][],
        elevation: garden.elevation,
        is_uncertain: garden.is_uncertain
    };

    updateGardenUncertainty(selectedPub.google_place_id, apiGarden, isUncertain)
        .then(response => {
            if (response.status === 200) {
                setSuccessMessage(`Garden "${garden.name || 'Garden'}" marked as ${isUncertain ? 'uncertain' : 'certain'}.`);
                clearPubCache(selectedPub.google_place_id); // Clear cache

                // Trigger reload to reflect change in GardenDraw3D list
                setTimeout(() => {
                    setReloadGardens(prev => prev + 1);
                    setSuccessMessage(''); // Clear after reload starts
                }, 800);
            } else {
                throw new Error(`Unexpected response status: ${response.status}`);
            }
        })
        .catch(error => {
            console.error('Error updating garden uncertainty:', error);
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            const detailedError = (axios.isAxiosError(error) && error.response?.data?.detail)
                ? error.response.data.detail
                : errorMsg;
            setErrorMessage(`Failed to update uncertainty: ${detailedError}`);
            setSuccessMessage('');
        })
        .finally(() => {
            setIsSubmitting(false);
        });
   }, [selectedPub]);

  // Handle Marking Pub as Having No Garden (from GardenDraw3D)
  const handlePubHasNoGarden = useCallback(() => {
    if (!selectedPub) {
      setErrorMessage('No pub selected. Cannot update.');
      return;
    }
    // Show confirmation dialog before proceeding
    setNoGardenConfirmDialog(true);
  }, [selectedPub]);

  const handleOpenAttribution = useCallback(() => {
    setAttributionOpen(true);
  }, []);

  // Confirm and Execute Marking Pub as No Garden
  const confirmNoGarden = useCallback(async () => {
    setNoGardenConfirmDialog(false); // Close dialog immediately
    if (!selectedPub) {
      setErrorMessage('Action cancelled: No pub selected.');
      return;
    }

    trackNoGardenMarked(
      selectedPub.google_place_id, 
      selectedPub.name, 
      false // Assuming this is not from a no-garden view since we're marking it
    );

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage(`Marking "${selectedPub.name}" as having no garden...`);

    try {
        const response = await markPubHasNoGarden(selectedPub.google_place_id);

        if (response.status === 200) {
            setSuccessMessage(`"${selectedPub.name}" has been marked as having no garden. Thank you!`);
            clearPubCache(selectedPub.google_place_id); // Clear cache

            // Update local state to reflect the change
            setSelectedPub(prevPub => prevPub ? { ...prevPub, has_no_garden: true } : null);
            // Trigger viewer reload to potentially hide garden list/drawing tools
            setTimeout(() => {
                setReloadGardens(prev => prev + 1);
                setSuccessMessage(''); // Clear message after state update
            }, 1200); // Longer delay to read message
        } else {
            throw new Error(`Unexpected response status: ${response.status}`);
        }
    } catch (error) {
        console.error('Error marking pub as having no garden:', error);
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        const detailedError = (axios.isAxiosError(error) && error.response?.data?.detail)
            ? error.response.data.detail
            : errorMsg;
        setErrorMessage(`Failed to update pub: ${detailedError}`);
        setSuccessMessage('');
    } finally {
        setIsSubmitting(false);
    }
  }, [selectedPub]);

  // Handle Back Navigation from Viewer
  const handleBack = () => {
    setShowViewer(false);
    setMeshUris([]);
    setSuccessMessage('');
    setErrorMessage('');
    setSelectedPub(null);
    setAdjustedFocalPoint(null); // Reset adjusted focal point on back
    setIsPubPreselected(false); // Allow search again
    setSearchQuery(''); // Clear search field
    setGardenName(''); // Clear any lingering garden info
    setLastDrawnGardenName('');
    setReloadGardens(prev => prev + 1); // Ensure viewer key changes if re-opened later
  };

  // Add this new helper function for processing the final submission with submitter name
  const processFinalSubmission = async (submission: any, name: string) => {
    try {
      setIsSubmitting(true);
      
      const response = await submitGardenToDatabase(
        selectedPub!.google_place_id,
        submission.convertedCoordinates,
        submission.gardenNameValue,
        submission.avgElevation,
        submission.isUncertain,
        submission.existingGardenUid,
        name
      );
      
      if (response.status === 200 || response.status === 201) {
        setSuccessMessage(`Garden "${submission.gardenNameValue}" successfully saved by ${name}!`);
        clearPubCache(selectedPub!.google_place_id); // Clear cache
        
        // Update selected pub state if garden_drawn status changed
        setSelectedPub(prev => prev ? { ...prev, garden_drawn: true, has_no_garden: false } : null);
        
        // Show the view results modal
        setShowViewResultsModal(true);
        
        // Trigger reload in GardenDraw3D after a short delay to show updated list
        setTimeout(() => {
          setReloadGardens(prev => prev + 1);
          setLastDrawnGardenName(''); // Clear last drawn name after successful save
        }, 800); // Delay allows user to see success message
      }
    } catch (error) {
      console.error('Error submitting garden data:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      const detailedError = (axios.isAxiosError(error) && error.response?.data?.detail)
        ? error.response.data.detail
        : errorMsg;
      setErrorMessage(`Failed to save garden: ${detailedError}`);
      setSuccessMessage(''); // Clear any optimistic message
    } finally {
      setIsSubmitting(false);
      setPendingSubmission(null);
    }
  };

  // --- JSX Rendering ---
  return (
    <Box sx={{ ...styles.pageContainer, position: 'relative' }}>
      <Fonts />
      {/* Update TopNav title based on state */}
      <TopNav title={showViewer && selectedPub ? `Draw Garden: ${selectedPub.name}` : 'Submit Pub Garden'} />

      <Container sx={{...styles.contentContainer }}>
        {!showViewer ? (
          // --- State 1: Searching for or Selecting a Pub ---
          <>
            {loading ? (
              // Show loader while pub data is being fetched
              <Loader message={`Loading pub data...`} marginTop="0" />
            ) : (
              <>
                {!isPubPreselected && !selectedPub && (
                  <Paper
                    elevation={0}
                    sx={{
                      ...styles.card,
                      marginTop: '-14vh'
                    }}
                  >
                    <Typography variant="h6" sx={styles.stepTitle}>
                      <SearchIcon sx={styles.stepIcon} /> FIND A PUB
                    </Typography>
                    
                    {/* Replace Autocomplete with PubSearchGoogle */}
                    <PubSearchGoogle
                      onPubSelect={handleGooglePubSelect}
                      placeholder="Search pub name or address..."
                      initialQuery={searchQuery}
                      onError={(error) => setErrorMessage(error as string)}
                      autoFocus={true}
                      containerSx={{ width: '100%', mb: 2 }}
                      inputSx={{
                        borderRadius: '40px',
                        width: '90%',
                        fontFamily: "DM Sans, sans-serif",
                        border: '2px solid black',
                        outline: 'none',
                        '.MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                          '.Mui-focused': {
                            border: 'none',
                          }
                        },
                        '.MuiOutlinedInput-input': {
                          fontFamily: "DM Sans, sans-serif",
                        },
                      }}
                      onLoading={(isLoading) => {}}
                    />
                  </Paper>
                )}

                {/* Display selected pub info before loading viewer */}
                {selectedPub && !showViewer && (
                  <Paper elevation={0} sx={{...styles.card, backgroundColor: themeColors.lightBlue}}>
                     <Typography variant="h6" sx={styles.stepTitle}>
                        <LocationOnIcon sx={styles.stepIcon} /> Selected Pub
                     </Typography>
                     <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                         <LocationOnIcon sx={{ mr: 1, color: themeColors.blue, fontSize: '1.2rem' }} />
                         <Typography variant="subtitle1" sx={styles.pubName} >
                             {selectedPub.name}
                         </Typography>
                     </Box>
                     {selectedPub.address && (
                        <Typography variant="body2" sx={{ ...styles.pubAddress, ml: '28px' }}>
                            {selectedPub.address}
                        </Typography>
                     )}
                     {/* Loading indicator while fetching mesh data */}
                     {fetchingMeshes && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">Loading 3D map...</Typography>
                        </Box>
                      )}
                     {/* Error message specific to mesh fetching */}
                     {errorMessage && !fetchingMeshes && (
                         <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>
                     )}
                  </Paper>
                )}
              </>
            )}
          </>
        ) : (
          // --- State 2: Showing the 3D Viewer ---
          <>
            {/* Back Button and Pub Info Header above the viewer */}
            {selectedPub && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, marginTop: '-16vh' }}>
                <IconButton
                  onClick={handleBack}
                  aria-label="back to search"
                  size="small"
                  sx={{
                    color: themeColors.orange,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    padding: '5px',
                    minWidth: '32px',
                    height: '32px',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 1)',
                      color: themeColors.mainYellow,
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    }
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>

                {/* Pub Info Paper */}
                <Paper elevation={0} sx={{
                  flexGrow: 1,
                  padding: '2px 12px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                }}>
                  <Typography
                    variant="subtitle1"
                    noWrap
                    title={selectedPub.name}
                    sx={{
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      lineHeight: 1.2,
                      color: themeColors.darkBackground,
                    }}
                  >
                    <LocationOnIcon sx={{ mr: 0.75, fontSize: '1rem', color: themeColors.blue }} />
                    {selectedPub.name}
                  </Typography>
                  {selectedPub.address &&
                    <Typography
                      variant="caption"
                      noWrap
                      title={selectedPub.address}
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.2,
                        fontSize: '0.75rem',
                        pl: '22px',
                         display: 'block',
                      }}
                    >
                      {selectedPub.address}
                    </Typography>
                  }
                </Paper>
              </Box>
            )}

            {/* Error Alert specifically for viewer/drawing issues */}
            {errorMessage && (
              <Alert
                severity="error"
                sx={{ ...styles.viewerMessage, mb: 1.5, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(253, 237, 237, 0.85)' }}
                onClose={() => setErrorMessage('')}
               >
                {errorMessage}
              </Alert>
            ) }


            {/* The 3D Viewer container */}
            <Paper elevation={1} sx={{ ...styles.viewerPaper, mt: 0, height: '65vh' }}>     
              {(selectedPub && meshUris.length > 0 && memoizedFocalPoint) ? (
                <GardenDraw3DController
                  meshUris={meshUris}
                  focalPoint={memoizedFocalPoint}
                  pubName={selectedPub.name}
                  googlePlaceId={selectedPub.google_place_id}
                  onPolygonComplete={handleGardenComplete}
                  onGardenDelete={handleGardenDelete}
                  onToggleUncertainty={handleToggleUncertainty}
                  onPubHasNoGarden={handlePubHasNoGarden}
                  pubHasNoGarden={selectedPub.has_no_garden}
                  onGardensLoaded={(gardens: Garden[]) => setGardenCount(gardens.length)}
                  handleGardenNaming={true}
                  showInitialHelp={true}
                  height="100%"
                  key={`viewer-${selectedPub.google_place_id}`}
                  reload={reloadGardens}
                  adjustFocalPointToMesh={true}
                  onFocalPointAdjusted={handleFocalPointAdjusted}
                  disableShadows={true}
                />
              ) : (
                <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary', p: 2}}>
                  {fetchingMeshes ? (
                    <>
                      <CircularProgress sx={{ mb: 1 }}/>
                      <Typography>Loading 3D View...</Typography>
                    </>
                  ) : (
                    <Typography>3D View unavailable or still loading.</Typography>
                  )}
                </Box>
              )}
            </Paper>

            {/* Attribution Button - Only shown if viewer is open and pub is selected */}
            {showViewer && selectedPub && (
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'flex-end',
                p: 0.25,
                mt: 0.5
              }}>
                <Button 
                  size="small" 
                  variant="text" 
                  onClick={handleOpenAttribution}
                  sx={{ 
                    fontSize: '0.7rem',
                    color: 'rgba(0,0,0,0.5)',
                    textTransform: 'none',
                    minWidth: 'auto',
                    padding: '1px 6px',
                    height: '20px',
                    lineHeight: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.03)',
                    }
                  }}
                >
                  Data sources
                </Button>
              </Box>
            )}
            
            {/* View Pub Results Button - Added as requested, only shown if there are gardens */}
            {selectedPub && showViewer && gardenCount > 0 && (
              <Box sx={{ mt: 1.5, mb: 1.5, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleViewResults}
                  startIcon={<VisibilityIcon />}
                  sx={{
                    backgroundColor: themeColors.orange,
                    '&:hover': { backgroundColor: themeColors.mainYellow },
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    padding: '8px 20px',
                  }}
                >
                  View Pub Results
                </Button>
              </Box>
            )}

             {/* Success/Info Alert (shown below viewer) */}
             {successMessage && (
              <Alert
                severity={isSubmitting ? "info" : "success"}
                sx={{
                  ...styles.viewerMessage,
                  mt: 1.5,
                  backdropFilter: 'blur(10px)',
                  backgroundColor: isSubmitting
                    ? 'rgba(229, 246, 253, 0.85)'
                    : 'rgba(237, 247, 237, 0.85)',
                  alignItems: 'center',
                }}
                onClose={() => !isSubmitting && setSuccessMessage('')}
                icon={isSubmitting ? <CircularProgress size={20} /> : undefined}
              >
                {successMessage}
              </Alert>
            )}
          </>
        )}
      </Container>

      {/* --- Dialogs --- */}

      {/* Overwrite/Create New Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="garden-confirm-dialog-title"
      >
        <DialogTitle id="garden-confirm-dialog-title">
          Garden Name Conflict
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            A garden named "{pendingSubmission?.gardenNameValue}" already exists for "{selectedPub?.name}".
            <br /><br />
            Would you like to overwrite the existing garden data or create a second garden with the same name?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfirmDialog} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Box>
            <Button onClick={handleCreateNewGarden} variant="contained" color="primary" sx={{ mr: 1 }}>
              Create New
            </Button>
            <Button onClick={handleOverwriteGarden} variant="contained" color="warning">
              Overwrite Existing
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* No Garden Confirmation Dialog */}
      <Dialog
        open={noGardenConfirmDialog}
        onClose={() => setNoGardenConfirmDialog(false)}
        aria-labelledby="no-garden-confirm-dialog-title"
      >
        <DialogTitle id="no-garden-confirm-dialog-title">
          Confirm No Garden Space
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please confirm that "{selectedPub?.name}" definitely has **no outdoor garden space** suitable for customers.
            <br /><br />
            This action helps keep our data accurate.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button onClick={() => setNoGardenConfirmDialog(false)} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Button onClick={confirmNoGarden} variant="contained" color="warning">
            Confirm - No Garden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attribution Dialog */}
      <Dialog
        open={attributionOpen}
        onClose={() => setAttributionOpen(false)}
        aria-labelledby="attribution-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxWidth: '90%',
            maxHeight: '80%'
          }
        }}
      >
        <DialogTitle id="attribution-dialog-title" sx={{ fontFamily: "'DM Sans', sans-serif" }}>
          Data Sources
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
            3D data is provided by the following sources:
          </Typography>
          <List sx={{ width: '100%' }}>
            {fetchingMeshes && (
              <ListItem>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 2, color: themeColors.blue }} />
                  <ListItemText 
                    primary="Loading attribution information..." 
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      style: { fontSize: '0.9rem' } 
                    }}
                  />
                </Box>
              </ListItem>
            )}
            
            {!fetchingMeshes && copyrights && copyrights.length > 0 && (
              copyrights.map((copyright: string, index: number) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary={copyright} 
                    primaryTypographyProps={{ 
                      variant: 'body2', 
                      style: { fontSize: '0.9rem' } 
                    }}
                  />
                </ListItem>
              ))
            )}
            
            {!fetchingMeshes && (!copyrights || copyrights.length === 0) && (
              <ListItem>
                <ListItemText 
                  primary="Attribution information not available" 
                  primaryTypographyProps={{ 
                    variant: 'body2',
                    style: { fontStyle: 'italic' }
                  }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setAttributionOpen(false)} 
            variant="contained" 
            size="small"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Results Modal - New addition */}
      <Dialog
        open={showViewResultsModal}
        onClose={() => setShowViewResultsModal(false)}
        aria-labelledby="view-results-dialog-title"
        PaperProps={{ sx: { borderRadius: 3, maxWidth: '450px' } }}
      >
        <DialogTitle id="view-results-dialog-title">Garden Saved Successfully</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Would you like to view the sun predictions for this pub now?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewResultsModal(false)} variant="outlined" color="secondary">
            Not Now
          </Button>
          <Button onClick={handleViewResults} variant="contained" color="primary" autoFocus sx={{
            backgroundColor: themeColors.orange,
            '&:hover': { backgroundColor: themeColors.mainYellow },
          }}>
            View Results
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submitter Name Modal */}
      <Dialog
        open={submitterNameModal}
        onClose={() => setSubmitterNameModal(false)}
        aria-labelledby="submitter-name-dialog-title"
        PaperProps={{ sx: { borderRadius: 3, maxWidth: '450px' } }}
      >
        <DialogTitle id="submitter-name-dialog-title">Who Are You?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide your name so we can credit you for this garden submission.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="submitter-name"
            label="Your Name"
            type="text"
            fullWidth
            variant="outlined"
            value={submitterName}
            onChange={(e) => setSubmitterName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (submitterName.trim() === '') {
              // Show error or validation message
              setErrorMessage('Please enter your name to continue.');
              return;
            }
            setSubmitterNameModal(false);
            
            // Process the pending submission with the submitter name
            if (pendingSubmission) {
              processFinalSubmission(pendingSubmission, submitterName);
            }
          }} variant="contained" color="primary" autoFocus>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Footer */}
      <Box sx={{ px: '5%' }}>
        <Footer />
      </Box>

    </Box>
  );
};

export default SubmitGarden3D;